import React, { useState, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import Message from 'shared/Message';
import ButtonRounded from 'shared/Button';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from 'contexts/auth';
import { environments } from 'constants/environments';
import { IApiMetaData } from 'interfaces/apiMetaData';
import { callApi } from 'apiServices/callApi';
import { changePasswordSchema, IChangePasswordInputs } from 'schema/admin';

const ChangePassword: React.FC = React.memo(() => {
  const [isResponse, setIsResponse] = useState(false);
  const [result, setResult] = useState<IApiMetaData | any>(null); // Set to IApiMetaData type instead of any
  const { sharedData } = useAuth();
  const changePasswordAPI = environments.domain + sharedData?.apisList?.Dashboard?.Auth?.changePassword;

  const { handleSubmit, register, formState: { errors, isSubmitting }, reset } = useForm<IChangePasswordInputs>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<IChangePasswordInputs> = useCallback(async (data) => {
    setIsResponse(false);
    const { confirmPassword, ...others } = data;

    try {
      const response: IApiMetaData = await callApi('PUT', changePasswordAPI, others, {}, {}, false);
      setResult(response);

      if (response.Status === 201) {
        reset(); // Reset form upon successful password change
      }

    } catch (error: any) {
      // Handle the error and set the error message in result
      setResult({
        Status: error.response?.status || 500,
        Message: error.response?.data?.Error || 'An error occurred while changing the password',
      });
    }

    setIsResponse(true);
  }, [changePasswordAPI, reset]);

  return (
    <div className="grid justify-content-center">
      <div className="col-12">
        <div className="px-3">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-fluid formgrid p-4 grid  border-round-md">
              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="email">Email</label>
                <InputText id="email" type="email" {...register('email')} />
                {errors.email && <small className='text-error text-red-600'>{errors.email.message}</small>}
              </div>

              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="oldPassword">Old Password</label>
                <InputText id="oldPassword" type="password" {...register('oldPassword')} />
                {errors.oldPassword && <small className='text-error text-red-600'>{errors.oldPassword.message}</small>}
              </div>

              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="newPassword">New Password</label>
                <InputText id="newPassword" type="password" {...register('newPassword')} />
                {errors.newPassword && <small className='text-error text-red-600'>{errors.newPassword.message}</small>}
              </div>

              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="confirm-password">Confirm Password</label>
                <InputText id="confirm-password" type="password" {...register('confirmPassword')} />
                {errors.confirmPassword && <small className='text-error text-red-600'>{errors.confirmPassword.message}</small>}
              </div>
            </div>

            <div className='text-right mt-4'>
              <ButtonRounded disabled={isSubmitting} label="Save" raised severity="success" />
            </div>
          </form>
        </div>
      </div>

      {isResponse && (
        <div className='col-8'>
          <Message
            message={result?.Message} // Always use the Message field for both success and error messages
            severity={result?.Status === 201 ? 'success' : 'error'}
          />
        </div>
      )}
    </div>
  );
});

export default ChangePassword;
